import {
  Flex,
  Heading,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Th,
  Thead,
  Td,
  Tr,
  Text,
} from '@chakra-ui/react';
import _ from 'lodash';
import React from 'react';
import { Link } from 'react-router-dom';

import {
  date as commonDate,
  finleyAssertUnreachable,
  utils as commonUtils,
} from '@finley-technologies/common';

import {
  AssignmentMethodGQLType,
  AssignmentPreviewJobFragmentGQLType,
  AssignmentPreviewJobStatusGQLType,
} from 'types/dashboard-core-gqlTypes.generated.d';

import { colors } from 'packages/common-frontend/colors';
import { Icon } from 'packages/common-frontend/components/icons';
import { frozenHeaderRowStyling } from 'packages/common-frontend/styles';

const TableHeader = ({
  name,
  style,
}: {
  name: string;
  style?: React.CSSProperties;
}) => {
  return (
    <Th style={style}>
      <Text
        color="black"
        fontSize="16px"
        padding="8px 0px"
        whiteSpace="pre-wrap"
        lineHeight="20px"
      >
        {name}
      </Text>
    </Th>
  );
};

type StatusProps = {
  icon: React.ReactElement;
  label: string;
  secondaryLabel?: string;
};
const Status = ({ icon, label, secondaryLabel }: StatusProps) => {
  return (
    <Flex alignItems="center">
      {icon}
      <Flex direction="column">
        <Text ml="8px" color="gray">
          {label}
        </Text>
        {secondaryLabel && (
          <Text ml="8px" color="gray">
            ({secondaryLabel})
          </Text>
        )}
      </Flex>
    </Flex>
  );
};

type StatusCellProps = {
  status: AssignmentPreviewJobStatusGQLType;
  completedAt?: number;
};
const StatusCell = ({ status, completedAt }: StatusCellProps) => {
  switch (status) {
    case 'inProgress':
      return <Status icon={<Spinner />} label="In Progress" />;
    case 'completed':
      return (
        <Status
          icon={
            <Icon icon="circle-check" color={colors.green[400]} size="medium" />
          }
          label="Complete"
          secondaryLabel={
            completedAt ? commonDate.epochToLocaleDateString(completedAt) : ''
          }
        />
      );
    case 'error':
      return (
        <Status
          icon={
            <Icon icon="circle-xmark" color={colors.red[400]} size="medium" />
          }
          label="Error"
        />
      );
    default:
      return finleyAssertUnreachable(status);
  }
};

type AssignmentMethodCellProps = {
  assignmentMethod: AssignmentMethodGQLType;
  strategyId: string | null;
  strategyName: string | null;
};
const AssignmentMethodCell = ({
  assignmentMethod,
  strategyId,
  strategyName,
}: AssignmentMethodCellProps) => {
  let cellText: string;
  switch (assignmentMethod) {
    case 'assignNone':
      cellText = 'Assign None';
      break;
    case 'csv':
      cellText = 'Assign by CSV';
      break;
    case 'strategy':
      cellText = `Assign by Strategy`;
      break;
    default:
      return finleyAssertUnreachable(assignmentMethod);
  }
  return (
    <Flex direction="column">
      <Text color="gray">{cellText}</Text>
      {assignmentMethod === 'strategy' && (
        <Text color="gray" whiteSpace="pre-wrap">
          <>
            (
            <Link
              style={{ color: '#137CBD', textDecoration: 'underline' }}
              to={`/settings/assignment-strategy/${strategyId}/edit`}
            >
              {strategyName}
            </Link>
            )
          </>
        </Text>
      )}
    </Flex>
  );
};

export const PreviewJobsTable = ({
  selectedPreviewJob,
  setSelectedPreviewJob,
  previewJobs,
}: {
  selectedPreviewJob: AssignmentPreviewJobFragmentGQLType;
  setSelectedPreviewJob: (
    previewJob: AssignmentPreviewJobFragmentGQLType,
  ) => void;
  previewJobs: AssignmentPreviewJobFragmentGQLType[];
}) => {
  return (
    <Flex direction="column">
      <Heading size="lg" mb="16px">
        Previews
      </Heading>
      <TableContainer
        borderWidth="1px"
        maxHeight="35vh"
        overflowX="auto"
        overflowY="auto"
      >
        <Table>
          <Thead>
            <Tr style={frozenHeaderRowStyling}>
              <TableHeader name="Status" />
              <TableHeader name="Assignment Method" />
              <TableHeader name="Initiated By" />
              <TableHeader name="Total Proposed Exposure (New)" />
              <TableHeader
                name="Proposed Borrowing Base From Receivables"
                style={{
                  minWidth: '185px',
                }}
              />
              <TableHeader
                name="Proposed Receivable Effective Advance Rate"
                style={{
                  minWidth: '205px',
                }}
              />
              <TableHeader name="Remaining on Balance Sheet" />
              <Th />
            </Tr>
          </Thead>
          <Tbody>
            {_.map(
              previewJobs,
              ({
                id,
                status,
                assignmentMethod,
                strategyId,
                strategyName,
                created_at: createdAt,
                completedAt,
                initiatedByUserIdentifier,
                newlyProposedExposure,
                totalProposedExposure,
                proposedBorrowingBaseFromReceivables,
                balanceSheetExposure,
              }) => {
                const isJobSelected = id === selectedPreviewJob.id;
                return (
                  <Tr
                    key={id}
                    onClick={() => {
                      const previewJob = previewJobs.find(
                        (job) => job.id === id,
                      );
                      if (previewJob) {
                        setSelectedPreviewJob(previewJob);
                      }
                    }}
                    backgroundColor={isJobSelected ? colors.aurora[50] : 'none'}
                    _hover={{
                      cursor: 'pointer',
                      backgroundColor: isJobSelected ? 'auto' : colors.gray[20],
                    }}
                  >
                    <Td minWidth="15vw" whiteSpace="pre-wrap">
                      <Flex direction="column">
                        <StatusCell
                          status={status}
                          completedAt={completedAt ?? undefined}
                        />
                      </Flex>
                    </Td>
                    <Td>
                      <AssignmentMethodCell
                        assignmentMethod={assignmentMethod}
                        strategyId={strategyId ?? null}
                        strategyName={strategyName ?? null}
                      />
                    </Td>
                    <Td minWidth="15vw" whiteSpace="pre-wrap">
                      <Flex direction="column">
                        <Text color="gray">{initiatedByUserIdentifier}</Text>
                        <Text color="gray">
                          ({commonDate.epochToLocaleDateString(createdAt)})
                        </Text>
                      </Flex>
                    </Td>
                    <Td>
                      <Flex direction="column">
                        {totalProposedExposure && (
                          <Text color="gray">
                            {commonUtils.numberToCurrencyWithSymbol({
                              number: totalProposedExposure,
                            })}
                          </Text>
                        )}
                        {newlyProposedExposure !== null &&
                          newlyProposedExposure !== undefined && (
                            <p>
                              <span style={{ color: colors.black[70] }}>(</span>
                              <span
                                style={{
                                  color:
                                    newlyProposedExposure >= 0
                                      ? colors.green[400]
                                      : colors.red[400],
                                }}
                              >{`${
                                newlyProposedExposure >= 0 ? '+' : '-'
                              }${commonUtils.numberToCurrencyWithSymbol({
                                number: newlyProposedExposure,
                              })}`}</span>
                              <span style={{ color: colors.black[70] }}>)</span>
                            </p>
                          )}
                      </Flex>
                    </Td>
                    <Td>
                      {proposedBorrowingBaseFromReceivables && (
                        <Text color="gray">
                          {commonUtils.numberToCurrencyWithSymbol({
                            number: proposedBorrowingBaseFromReceivables,
                          })}
                        </Text>
                      )}
                    </Td>
                    <Td>
                      {proposedBorrowingBaseFromReceivables &&
                        totalProposedExposure && (
                          <Text color="gray">
                            {commonUtils.numberToFormattedString(
                              proposedBorrowingBaseFromReceivables /
                                totalProposedExposure,
                              'percentage',
                              false,
                            )}
                          </Text>
                        )}
                    </Td>
                    <Td>
                      <Text color="gray">
                        {balanceSheetExposure && (
                          <Text color="gray">
                            {commonUtils.numberToCurrencyWithSymbol({
                              number: balanceSheetExposure,
                            })}
                          </Text>
                        )}
                      </Text>
                    </Td>
                    <Td>
                      <Flex direction="row-reverse">
                        <Icon icon="chevron-right" color={colors.info} />
                      </Flex>
                    </Td>
                  </Tr>
                );
              },
            )}
          </Tbody>
        </Table>
      </TableContainer>
    </Flex>
  );
};
